import { Routes, Route, Outlet, useNavigate, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { NotificationProvider, useNotification } from '../components/NotificationContext';
import { useSubscription } from '../components/SubscriptionContext';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import Files from './Files';
import SpeechToText from './SpeechToText';
import TextToSpeech from './TextToSpeech';
import FaceDetection from './FaceDetection';
import AnalyzeImage from './AnalyzeImage';
import Profile from './Profile';
import ChatBotPage from './ChatBotPage';
import './Dashboard.css';
import PropTypes from 'prop-types';
import FileUpload from './FileUpload';
import FileList from './FileList';
import FileDownload from './FileDownload';
import FileDelete from './FileDelete';
import FileStats from './FileStats';
import FilePreview from './FilePreview';
import AdminPushMessage from './AdminPushMessage';
import Sms from './Sms';
import Calls from './Calls';
import ImageGeneration from './ImageGeneration';
import VideoGeneration from './VideoGeneration';
import DashboardOverview from './DashboardOverview';

const DashboardHome = ({
  username,
  greeting,
  isSmsUnlocked,
  isCallUnlocked,
  isImageUnlocked,
  onUnlockSms,
  onUnlockCall,
  onUnlockImage,
}) => {
  const { triggerNotification } = useNotification();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (localStorage.getItem('firstLogin') !== 'false') {
      triggerNotification();
      localStorage.setItem('firstLogin', 'false');
    }
  }, [triggerNotification]);

  return (
    <div className="dashboard-home beautiful-dashboard">
      <div className="dashboard-bubbles">
        <span className="dash-bubble db-1" />
        <span className="dash-bubble db-2" />
        <span className="dash-bubble db-3" />
        <span className="dash-bubble db-4" />
      </div>
      <div className="dashboard-welcome-card animate-fadein">
        <h2>{greeting}, {username}!</h2>
        <p>Welcome to <strong>Servantica</strong> — Your all-in-one digital workspace</p>
      </div>
      <div className="dashboard-quick-actions">
        <div className="qa-card gradient-lilac animate-pop">
          <div className="qa-header">
            <span className="qa-icon">📩</span>
            <div>
              <p className="qa-label">SMS</p>
              <p className="qa-title">Send and schedule messages</p>
            </div>
          </div>
          <p className="qa-copy">Generate and reuse Auth0 tokens, deliver instantly, or plan ahead.</p>
          <div className="qa-actions">
            <span className={`status-pill ${isSmsUnlocked ? 'live' : 'locked'}`}>
              {isSmsUnlocked ? 'Unlocked' : 'Locked · ₹5' }
            </span>
            <button
              type="button"
              className="qa-btn"
              onClick={() => (isSmsUnlocked ? navigate('/dashboard/sms?mode=send') : onUnlockSms?.())}
            >
              {isSmsUnlocked ? 'Open SMS' : 'Unlock SMS'}
            </button>
          </div>
        </div>

        <div className="qa-card gradient-azure animate-pop delay-1">
          <div className="qa-header">
            <span className="qa-icon">📞</span>
            <div>
              <p className="qa-label">Calls</p>
              <p className="qa-title">Place outbound calls</p>
            </div>
          </div>
          <p className="qa-copy">Get a call token, place outbound calls, and monitor status with one click.</p>
          <div className="qa-actions">
            <span className={`status-pill ${isCallUnlocked ? 'live' : 'locked'}`}>
              {isCallUnlocked ? 'Unlocked' : 'Locked · ₹5' }
            </span>
            <button
              type="button"
              className="qa-btn"
              onClick={() => (isCallUnlocked ? navigate('/dashboard/calls?mode=call') : onUnlockCall?.())}
            >
              {isCallUnlocked ? 'Open Calls' : 'Unlock Calls'}
            </button>
          </div>
        </div>

        <div className="qa-card gradient-mauve animate-pop delay-2">
          <div className="qa-header">
            <span className="qa-icon">🎨</span>
            <div>
              <p className="qa-label">Text to Image</p>
              <p className="qa-title">Generate AI images</p>
            </div>
          </div>
          <p className="qa-copy">Create unique images from your prompts. One-time unlock at ₹5.</p>
          <div className="qa-actions">
            <span className={`status-pill ${isImageUnlocked ? 'live' : 'locked'}`}>
              {isImageUnlocked ? 'Unlocked' : 'Locked · ₹5 to unlock' }
            </span>
            <button
              type="button"
              className="qa-btn"
              onClick={() => (isImageUnlocked ? navigate('/dashboard/image-generation') : onUnlockImage?.())}
            >
              {isImageUnlocked ? 'Open Text to Image' : 'Unlock Now'}
            </button>
          </div>
        </div>

        <div className="qa-card gradient-voice animate-pop delay-3">
          <div className="qa-header">
            <span className="qa-icon">🎙️</span>
            <div>
              <p className="qa-label">Voice Assistance</p>
              <p className="qa-title">Open the hands-free assistant</p>
            </div>
          </div>
          <p className="qa-copy">Use the dedicated Servantica voice assistance page for wake-word guidance and hands-free navigation.</p>
          <div className="qa-actions">
            <span className="status-pill live">Available</span>
            <button
              type="button"
              className="qa-btn qa-btn-voice"
              onClick={() => navigate('/assistant')}
            >
              Open Voice Assistance
            </button>
          </div>
        </div>
      </div>
      <div className="dashboard-context-row">
        <div className="dashboard-context-card">
          <h3>🚀 Quick Start</h3>
          <p>Upload, manage, and analyze your files with just a few clicks. Access powerful AI tools and keep your data secure in the cloud.</p>
        </div>
        <div className="dashboard-context-card">
          <h3>🔔 Notifications</h3>
          <p>Stay up to date with important alerts and updates. Click the bell icon in the sidebar to view your latest notifications.</p>
        </div>
        <div className="dashboard-context-card">
          <h3>🤖 AI Services</h3>
          <p>Speech-to-Text, Text-to-Speech, Face Detection, Image Analysis. Unlock AI to use these tools.</p>
          <div className="context-chip-row">
            <span className="context-chip">Speech → Text</span>
            <span className="context-chip">Text → Speech</span>
            <span className="context-chip">Face Detection</span>
            <span className="context-chip">Image Analysis</span>
          </div>
        </div>
        <div className="dashboard-context-card">
          <h3>📂 File Management</h3>
          <p>Upload, download, and organize your files. View usage stats and manage your storage efficiently.</p>
        </div>
        <div className="dashboard-context-card">
          <h3>🪄 Prompt Services</h3>
          <p>Generate media from text prompts. Unlock once to use these generators.</p>
          <div className="context-chip-row">
            <span className="context-chip">Text → Image</span>
            <span className="context-chip">Text → Video</span>
          </div>
        </div>
      </div>

      <div className="feature-deep-dive animate-fadein">
        <div className="deep-dive-header">
          <p className="usage-label">Servantica in depth</p>
          <h3>Every feature, explained and animated</h3>
          <p className="deep-sub">See how the workspace snaps together—telephony, AI lab, prompts, files, and assistants all in one motion.</p>
        </div>
        <div className="deep-dive-grid">
          <div className="deep-card shimmer delay-0">
            <div className="deep-icon">📂</div>
            <div>
              <h4>File Vault</h4>
              <p>Upload, preview, download, delete, and track stats across your storage with instant previews.</p>
              <div className="tag-row">
                <span className="tag">Upload & Download</span>
                <span className="tag">Preview</span>
                <span className="tag">Stats</span>
                <span className="tag">Cleanup</span>
              </div>
            </div>
          </div>

          <div className="deep-card shimmer delay-1">
            <div className="deep-icon">🧠</div>
            <div>
              <h4>AI Lab</h4>
              <p>Speech↔Text, Face Detection, Image Analysis—use Auth0-backed machine tokens to secure every call.</p>
              <div className="tag-row">
                <span className="tag">Speech → Text</span>
                <span className="tag">Text → Speech</span>
                <span className="tag">Face Detect</span>
                <span className="tag">Vision</span>
              </div>
            </div>
          </div>

          <div className="deep-card shimmer delay-2">
            <div className="deep-icon">🪄</div>
            <div>
              <h4>Prompt Studio</h4>
              <p>Text → Image and Text → Video live in one unlock. Keep prompts, retry runs, and measure usage.</p>
              <div className="tag-row">
                <span className="tag">Text → Image</span>
                <span className="tag">Text → Video</span>
                <span className="tag">Usage</span>
                <span className="tag">One-time Unlock</span>
              </div>
            </div>
          </div>

          <div className="deep-card shimmer delay-3">
            <div className="deep-icon">📞</div>
            <div>
              <h4>Comms Desk</h4>
              <p>Generate call and SMS tokens on demand, send instantly or schedule, and monitor delivery states.</p>
              <div className="tag-row">
                <span className="tag">SMS</span>
                <span className="tag">Calls</span>
                <span className="tag">Scheduling</span>
                <span className="tag">Delivery</span>
              </div>
            </div>
          </div>

          <div className="deep-card shimmer delay-4">
            <div className="deep-icon">🔔</div>
            <div>
              <h4>Notifications & Assistants</h4>
              <p>Push admin alerts, keep bell inbox tidy, and launch voice/chat assistants anywhere in the dashboard.</p>
              <div className="tag-row">
                <span className="tag">Admin Push</span>
                <span className="tag">Voice</span>
                <span className="tag">Chatbot</span>
                <span className="tag">Inbox</span>
              </div>
            </div>
          </div>
        </div>

        <div className="motion-strip">
          <div className="motion-track">
            <span className="motion-pill">Real-time tokens</span>
            <span className="motion-pill">Secure Auth0 flow</span>
            <span className="motion-pill">One-time prompt unlock</span>
            <span className="motion-pill">Usage overview</span>
            <span className="motion-pill">Files with preview</span>
            <span className="motion-pill">Voice + Chat assistants</span>
            <span className="motion-pill">SMS + Calls scheduling</span>
            <span className="motion-pill">AI lab ready</span>
          </div>
        </div>
      </div>

      <div className="feature-vertical animate-fadein">
        <p className="usage-label">Full stack lineup</p>
        <h3>All Servantica features at a glance</h3>
        <div className="vertical-list">
          <div className="vertical-item vi-1">
            <span className="vertical-icon">📩</span>
            <div>
              <p className="vertical-title">SMS · Send & Schedule</p>
              <p className="vertical-copy">Ship instant sends or timed campaigns with Auth0-backed tokens.</p>
            </div>
          </div>
          <div className="vertical-item vi-2">
            <span className="vertical-icon">📞</span>
            <div>
              <p className="vertical-title">Calls · Outbound & Scheduled</p>
              <p className="vertical-copy">Place outbound calls, monitor status, and schedule follow-ups.</p>
            </div>
          </div>
          <div className="vertical-item vi-3">
            <span className="vertical-icon">🪄</span>
            <div>
              <p className="vertical-title">Prompt Services · Text → Image</p>
              <p className="vertical-copy">Generate visuals from prompts with the one-time prompt unlock.</p>
            </div>
          </div>
          <div className="vertical-item vi-4">
            <span className="vertical-icon">🎥</span>
            <div>
              <p className="vertical-title">Prompt Services · Text → Video</p>
              <p className="vertical-copy">Create motion from text, side-by-side with image generation.</p>
            </div>
          </div>
          <div className="vertical-item vi-5">
            <span className="vertical-icon">🤖</span>
            <div>
              <p className="vertical-title">AI · Speech to Text</p>
              <p className="vertical-copy">Turn audio into transcripts with secure machine tokens.</p>
            </div>
          </div>
          <div className="vertical-item vi-6">
            <span className="vertical-icon">🗣️</span>
            <div>
              <p className="vertical-title">AI · Text to Speech</p>
              <p className="vertical-copy">Natural voice output for your scripts and prompts.</p>
            </div>
          </div>
          <div className="vertical-item vi-7">
            <span className="vertical-icon">🧠</span>
            <div>
              <p className="vertical-title">AI · Face Detection</p>
              <p className="vertical-copy">Detect faces securely to power verification or analysis flows.</p>
            </div>
          </div>
          <div className="vertical-item vi-8">
            <span className="vertical-icon">🖼️</span>
            <div>
              <p className="vertical-title">AI · Image Analysis</p>
              <p className="vertical-copy">Extract insights from images with your cloud-safe toolkit.</p>
            </div>
          </div>
          <div className="vertical-item vi-9">
            <span className="vertical-icon">📂</span>
            <div>
              <p className="vertical-title">Files · Upload & Manage</p>
              <p className="vertical-copy">Organize uploads, downloads, previews, and cleanups.</p>
            </div>
          </div>
          <div className="vertical-item vi-10">
            <span className="vertical-icon">📊</span>
            <div>
              <p className="vertical-title">Files · Stats & Preview</p>
              <p className="vertical-copy">View file metrics and preview media inline.</p>
            </div>
          </div>
          <div className="vertical-item vi-11">
            <span className="vertical-icon">🔔</span>
            <div>
              <p className="vertical-title">Notifications · Admin Push</p>
              <p className="vertical-copy">Broadcast updates to every user inbox instantly.</p>
            </div>
          </div>
          <div className="vertical-item vi-12">
            <span className="vertical-icon">💬</span>
            <div>
              <p className="vertical-title">Chatbot · Assistant</p>
              <p className="vertical-copy">Launch the assistant to guide users anywhere in the dashboard.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

DashboardHome.propTypes = {
  username: PropTypes.string.isRequired,
  greeting: PropTypes.string.isRequired,
  isSmsUnlocked: PropTypes.bool,
  isCallUnlocked: PropTypes.bool,
  isImageUnlocked: PropTypes.bool,
  onUnlockSms: PropTypes.func,
  onUnlockCall: PropTypes.func,
  onUnlockImage: PropTypes.func,
};

const Notifications = () => {
  const { notifications, markNotificationAsRead, clearNotifications } = useNotification();

  const handleNotificationClick = (id) => {
    markNotificationAsRead(id);
    window.localStorage.setItem('servantica_login_notif_seen', 'true');
  };

  return (
    <div className="notifications-page">
      <div className="notifications-header">
        <h1 className="profile-title">Notifications</h1>
        {notifications.length > 0 && (
          <button 
            onClick={clearNotifications}
            className="clear-notifications-btn"
          >
            Clear All
          </button>
        )}
      </div>
      <div className="notifications-list">
        {notifications.length === 0 && <div className="profile-details">No new notifications.</div>}
        {notifications.map((notif) => (
          <div
            key={notif.id}
            className={`notification-alert animate-notification ${notif.type || ''} ${notif.read ? 'notification-read' : 'notification-unread'}`}
            onClick={() => handleNotificationClick(notif.id)}
            style={{ cursor: !notif.read ? 'pointer' : 'default' }}
          >
            <div className="notification-content">
              <span className="notification-message">{typeof notif.message === 'string' ? notif.message : JSON.stringify(notif.message)}</span>
              <span className="notification-time">{new Date(notif.timestamp).toLocaleString()}</span>
            </div>
            {!notif.read && <span className="notif-alert-anim">!</span>}
          </div>
        ))}
      </div>
    </div>
  );
};

const DashboardLayout = () => (
  <main className="main-content">
    <Outlet />
    <Footer />
  </main>
);

const Dashboard = () => {
  const [greeting, setGreeting] = useState('');
  const { user } = useAuth0();
  const { isFeatureUnlocked, subscribeForFeature } = useSubscription();
  const storedUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('user')) || {};
    } catch (e) {
      return {};
    }
  })();
  const username = user?.name || user?.nickname || storedUser.name || storedUser.email || 'User';
  const enableFiles = process.env.REACT_APP_ENABLE_FILES === 'true';

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting('Good Morning 🌅');
    else if (hour < 18) setGreeting('Good Afternoon ☀️');
    else setGreeting('Good Evening 🌙');
  }, []);

  return (
    <NotificationProvider>
      <div className="dashboard">
        <Sidebar />
        <Routes>
          <Route path="/" element={<DashboardLayout />}>
            <Route
              index
              element={( 
                <DashboardHome
                  greeting={greeting}
                  username={username}
                  isSmsUnlocked={isFeatureUnlocked('sms')}
                  isCallUnlocked={isFeatureUnlocked('call')}
                  isImageUnlocked={isFeatureUnlocked('prompt-services')}
                  onUnlockSms={() => subscribeForFeature('sms')}
                  onUnlockCall={() => subscribeForFeature('call')}
                  onUnlockImage={() => subscribeForFeature('prompt-services')}
                />
              )}
            />
            <Route path="overview" element={<DashboardOverview />} />
            <Route
              path="home"
              element={( 
                <DashboardHome
                  greeting={greeting}
                  username={username}
                  isSmsUnlocked={isFeatureUnlocked('sms')}
                  isCallUnlocked={isFeatureUnlocked('call')}
                  isImageUnlocked={isFeatureUnlocked('prompt-services')}
                  onUnlockSms={() => subscribeForFeature('sms')}
                  onUnlockCall={() => subscribeForFeature('call')}
                  onUnlockImage={() => subscribeForFeature('prompt-services')}
                />
              )}
            />
            <Route path="profile" element={<Profile />} />
            <Route path="notifications" element={<Notifications />} /> {/* Now using the separate Notifications component */}
            {enableFiles && <Route path="files" element={<Files />} />}
            {enableFiles && <Route path="file-upload" element={<FileUpload />} />}
            {enableFiles && <Route path="file-list" element={<FileList />} />}
            {enableFiles && <Route path="file-download" element={<FileDownload />} />}
            {enableFiles && <Route path="file-delete" element={<FileDelete />} />}
            {enableFiles && <Route path="file-stats" element={<FileStats />} />}
            {enableFiles && <Route path="file-preview" element={<FilePreview />} />}
            {enableFiles && <Route path="speech-to-text" element={<SpeechToText />} />}
            {enableFiles && <Route path="text-to-speech" element={<TextToSpeech />} />}
            {enableFiles && <Route path="face-detection" element={<FaceDetection />} />}
            {enableFiles && <Route path="image-analysis" element={<AnalyzeImage />} />}
            <Route path="image-generation" element={<ImageGeneration />} />
            <Route path="video-generation" element={<VideoGeneration />} />
            <Route path="sms" element={<Sms />} />
            <Route path="calls" element={<Calls />} />
            <Route path="admin/push-message" element={<AdminPushMessage />} />
            <Route path="chatbot" element={<ChatBotPage />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Route>
        </Routes>
      </div>
    </NotificationProvider>
  );
};

export default Dashboard;